import * as Sentry from '@sentry/browser';
import type { MutationFunction } from '@apollo/client';
import type {
  CreateVisitorCustomFieldConfigMutation,
  CreateVisitorCustomFieldConfigMutationVariables,
  UpdateVisitorCustomFieldConfigStatusMutation,
  UpdateVisitorCustomFieldConfigStatusMutationVariables,
  VisitorCustomFieldConfigInput,
} from 'graphql-query-contracts';

import type { CustomFieldFormData } from '../../../../feature/components/CustomFieldsSection/customField.types';
import { isVisitorCustomFieldSelectType } from 'domains/visitorManagement/feature/components/CustomFieldsSection/customField.constants';

export const NO_ORGANIZATION_KEY = 'web.admin.visitorManagement.customField.error.noOrganization';
export const CUSTOM_FIELD_CREATED_SUCCESS_KEY = 'web.admin.visitorManagement.customField.success.created';
export const CUSTOM_FIELD_LOAD_CONFIGS_ERROR_KEY = 'web.admin.visitorManagement.customField.error.loadConfigs';
export const CUSTOM_FIELD_CONFIG_NOT_FOUND_KEY = 'web.admin.visitorManagement.customField.error.configNotFound';
export const MAX_ENABLED_FIELDS_REACHED_KEY = 'web.admin.visitorManagement.customField.validation.maxEnabledFieldsReached';
export const MAX_TOTAL_FIELDS_REACHED_KEY = 'web.admin.visitorManagement.customField.validation.maxTotalFieldsReached';

const CUSTOM_FIELD_SAVE_ERROR_KEY = 'web.admin.visitorManagement.customField.error.saving';
const CUSTOM_FIELD_UPDATE_STATUS_ERROR_KEY =
  'web.admin.visitorManagement.customField.error.updateConfigStatus';
const CUSTOM_FIELD_STATUS_ENABLED_SUCCESS_KEY =
  'web.admin.visitorManagement.customField.success.enabled';
const CUSTOM_FIELD_STATUS_DISABLED_SUCCESS_KEY =
  'web.admin.visitorManagement.customField.success.disabled';

type CreateFieldMutationFn = MutationFunction<
  CreateVisitorCustomFieldConfigMutation,
  CreateVisitorCustomFieldConfigMutationVariables
>;

type UpdateStatusMutationFn = MutationFunction<
  UpdateVisitorCustomFieldConfigStatusMutation,
  UpdateVisitorCustomFieldConfigStatusMutationVariables
>;

function flattenGraphQLErrors(
  errors: ReadonlyArray<{ message: string }> | undefined
): string {
  if (!errors?.length) {
    return '';
  }

  return errors.map(error => error.message).filter(Boolean).join(', ');
}

function toastWithOptionalDetail(
  showToast: (message: string) => void,
  summary: string,
  detail: string
): void {
  const trimmed = detail.trim();
  const toastMsg = trimmed && trimmed !== summary ? `${summary}: ${trimmed}` : summary;

  showToast(toastMsg);
}

export function buildCreateFieldConfig(
  formData: CustomFieldFormData,
  organizationId: string
): VisitorCustomFieldConfigInput {
  const options = isVisitorCustomFieldSelectType(formData.fieldType) ? formData.options : [];

  return {
    organizationId,
    fieldKey: formData.fieldKey,
    fieldName: formData.fieldName,
    fieldType: formData.fieldType,
    placeholder: formData.placeholder || undefined,
    options,
  };
}

export async function runCreateCustomFieldMutation({
  createField,
  config,
  t,
  showToast,
  onSuccess,
}: {
  createField: CreateFieldMutationFn;
  config: VisitorCustomFieldConfigInput;
  t: (key: string) => string;
  showToast: (message: string) => void;
  onSuccess: () => Promise<void> | void;
}): Promise<boolean> {
  const savingMessage = t(CUSTOM_FIELD_SAVE_ERROR_KEY);

  try {
    const result = await createField({ variables: { config } });
    const errorDetail = flattenGraphQLErrors(result.errors);

    if (errorDetail) {
      toastWithOptionalDetail(showToast, savingMessage, errorDetail);

      return false;
    }

    if (!result.data?.createVisitorCustomFieldConfig) {
      showToast(savingMessage);

      return false;
    }
  } catch (error) {
    toastWithOptionalDetail(
      showToast,
      savingMessage,
      error instanceof Error ? error.message : String(error)
    );

    return false;
  }

  try {
    await onSuccess();
  } catch (error) {
    // non-fatal: field was saved, side-effect failure doesn't change that
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
      { tags: { fn: 'runCreateCustomFieldMutation' } }
    );
  }

  return true;
}

export async function runUpdateVisitorCustomFieldStatus({
  updateFieldStatus,
  showToast,
  t,
  orgId,
  id,
  isEnabled,
}: {
  updateFieldStatus: UpdateStatusMutationFn;
  showToast: (message: string) => void;
  t: (key: string) => string;
  orgId: string;
  id: string;
  isEnabled: boolean;
}): Promise<boolean> {
  const updatingMessage = t(CUSTOM_FIELD_UPDATE_STATUS_ERROR_KEY);

  try {
    const result = await updateFieldStatus({
      variables: { input: { organizationId: orgId, id, isEnabled } },
    });
    const errorDetail = flattenGraphQLErrors(result.errors);

    if (errorDetail) {
      toastWithOptionalDetail(showToast, updatingMessage, errorDetail);

      return false;
    }

    if (!result.data?.updateVisitorCustomFieldConfigStatus) {
      showToast(updatingMessage);

      return false;
    }

    showToast(
      isEnabled
        ? t(CUSTOM_FIELD_STATUS_ENABLED_SUCCESS_KEY)
        : t(CUSTOM_FIELD_STATUS_DISABLED_SUCCESS_KEY)
    );

    return true;
  } catch (error) {
    toastWithOptionalDetail(
      showToast,
      updatingMessage,
      error instanceof Error ? error.message : String(error)
    );

    return false;
  }
}
